import React, { useState, useRef, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { Languages } from "../../Utils/Languages";
import { ConfigContext } from "../../context/GlobalContext";
import AddLedgerCustomerDataInFirebase from "../../data/Firebase/Customers/AddLedgerCustomerDataInFirebase";
import * as actionType from "../../context/actions";
import { v4 as uuidv4 } from "uuid";

const CustomerDetailsPopup = ({ show, onClose, customer, ledgerData }) => {
  const { state, dispatch } = useContext(ConfigContext);
  const dropdownRef = useRef(null);

  const paymentModes = ["cash", "UPI", "cheque", "card", "electronics"].map(
    (m) => Languages[state?.LANGUAGE]?.[m] || m
  );

  // Helper: convert timestamp → yyyy-mm-dd for input
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const d = new Date(Number(timestamp));
    return d.toISOString().split("T")[0];
  };

  // State
  const currencyDropdownRef = useRef(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(""); // yyyy-mm-dd string
  const [mode, setMode] = useState("Cash");
  const [notes, setNotes] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [error, setError] = useState({ amount: "", paymentMode: "" });



  // Prefill fields when editing
  useEffect(() => {
    if (ledgerData) {
      setAmount(
        ledgerData.credit || ledgerData.debit || ledgerData.totalAmount || ""
      );
      setDate(ledgerData.date ? formatDate(ledgerData.date) : "");
      setMode(ledgerData.paymentMode || "");
      setNotes(ledgerData.note || "");
      setCurrency(ledgerData.currency || customer?.currency || state?.CURRENCYCODE || "INR");
    } else {
      setAmount("");
      // Default to today’s date
      const today = new Date();
      const todayStr = today.toISOString().split("T")[0];
      setDate(todayStr);
      setMode("Cash");
      setNotes("");
      const activeFilter = state?.CUSTOMER_CURRENCY_FILTER;
      setCurrency(activeFilter !== "All" && activeFilter ? activeFilter : (customer?.currency || state?.CURRENCYCODE || "INR"));
    }
  }, [ledgerData, customer, state?.CUSTOMER_CURRENCY_FILTER]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setShowCurrencyDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if payment mode is selected
    if (!amount) {
      setError(prev => ({ ...prev, amount: "Amount is required" }));
      return;
    } else if (Number(amount) <= 0) {
      setError(prev => ({ ...prev, amount: "Amount must be greater than 0" }));
      return;
    }
    else {
      setError(prev => ({ ...prev, amount: "" }));
    }

    // 2️⃣ Validate payment mode
    if (!mode) {
      setError(prev => ({ ...prev, paymentMode: "Payment mode is required!" }));
      return;
    } else {
      setError(prev => ({ ...prev, paymentMode: "" }));
    }

    const isEditing = !!ledgerData?.id;

    let paymentDate;
    if (isEditing) {
      if (date && formatDate(ledgerData.date) !== date) {
        const selectedDate = new Date(date);
        const now = new Date();
        selectedDate.setHours(
          now.getHours(),
          now.getMinutes(),
          now.getSeconds(),
          now.getMilliseconds()
        );
        paymentDate = selectedDate.getTime();
      } else {
        paymentDate = ledgerData.date;
      }
    } else {
      const selectedDate = date ? new Date(date) : new Date();
      const now = new Date();
      selectedDate.setHours(
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds()
      );
      paymentDate = selectedDate.getTime();
    }

    const paymentRecord = {
      id: isEditing ? ledgerData.id : uuidv4(),
      date: paymentDate,
      updatedAt: isEditing ? Date.now() : null,
      type: "credit",
      note: notes || "",
      amount: Number(amount) || 0,
      paymentMode: mode,
      currency: currency,
    };

    try {
      await AddLedgerCustomerDataInFirebase(
        state,
        dispatch,
        {
          customer: customer?.customer,
          customerId: customer?.customerId,
          ledger: paymentRecord,
        },
        state?.USERID,
        isEditing
      );

      let updatedLedger;
      if (isEditing) {
        updatedLedger = (state?.SELECTEDCUSTOMER?.paymentsReceived || []).map((entry) =>
          entry.id === ledgerData.id ? paymentRecord : entry
        );
      } else {
        updatedLedger = [...(state?.SELECTEDCUSTOMER?.paymentsReceived || []), paymentRecord];
      }

      dispatch({
        type: actionType.SELECTEDCUSTOMER,
        payload: { ...state.SELECTEDCUSTOMER, paymentsReceived: updatedLedger },
      });

      setAmount("");
      setDate("");
      setMode("");
      setNotes("");
      onClose();
    } catch (error) {
      console.error("🔥 Error saving payment:", error);
    }
  };




  if (!show) return null;

  return createPortal(
    <>
      <div className="modal-backdrop fade show"></div>

      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow">
            <div className="modal-header">
              <h5 className="modal-title">
                {ledgerData?.id ? "Edit Payment" : "Add Payment"} for{" "}
                {customer?.customer || "Customer"}
              </h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Amount */}
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Paid Amount (in {currency}) <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control ${error.amount ? "is-invalid" : ""}`}
                    placeholder={`Enter Amount in ${currency}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <div className="form-text text-primary fs-12 fw-medium mt-1">
                    This payment is in {currency}
                  </div>
                  {error.amount && <div className="invalid-feedback">{error.amount}</div>}
                </div>

                {/* Date */}
                <div className="mb-3">
                  <label className="form-label">Payment Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                {/* Payment Mode */}
                <div className="mb-3">
                  <label className="form-label">Payment Mode <span className="text-danger"> *</span></label>
                  <div className="position-relative w-100" ref={dropdownRef}>
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setShowDropdown((prev) => !prev)}
                      style={{
                        background: "transparent",
                        outline: "none",
                        fontSize: 14,
                        paddingLeft: 20,
                        font: "inherit",
                      }}
                    >
                      {mode
                        ? mode.charAt(0).toUpperCase() + mode.slice(1)
                        : "Select Payment Mode"}
                    </button>
                    {error.paymentMode && (
                      <div className="text-danger small mt-1">
                        {error.paymentMode}
                      </div>
                    )}
                    {showDropdown && (
                      <ul
                        className="dropdown-menu show w-100"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          padding: "10px",
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          zIndex: 1050,
                        }}
                      >
                        {paymentModes.map((pm, index) => (
                          <li key={index}>
                            <button
                              type="button"
                              className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${mode === pm ? "bg-primary text-white" : ""
                                }`}
                              onClick={() => {
                                setMode(pm);
                                  setShowDropdown(false);
                                  setError(prev => ({ ...prev, paymentMode: "" }));
                              }}
                            >
                              {pm}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>





                {/* Notes */}
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional remarks..."
                  ></textarea>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {ledgerData?.id ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CustomerDetailsPopup;
